import React from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import VideocamOutlinedIcon from 'components/material-symbol/icons/Videocam'
import SectionEmptyState from 'components/section-empty-state'
import RequestStatusChip from './request-status-chip'
import VideoThumbnail from './video-thumbnail'
import {
  formatDate,
  formatTurnaroundDuration,
  getAgingLabel,
  getRequestPath,
  getStudentName,
  getTurnaroundHours,
  isOverdue,
  REQUEST_TABS,
} from './utils'

const useStyles = makeStyles(theme => ({
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  headCell: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  overdue: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  emptyState: {
    padding: theme.spacing(4, 2),
  },
}))

const COLUMNS = {
  [REQUEST_TABS.PURCHASED]: [
    { id: 'status', label: 'Status' },
    { id: 'golfer', label: 'Golfer' },
    { id: 'purchased', label: 'Purchased' },
    { id: 'credits', label: 'Outstanding swing credits', align: 'right' },
  ],
  [REQUEST_TABS.READY]: [
    { id: 'status', label: 'Status' },
    { id: 'golfer', label: 'Golfer' },
    { id: 'video', label: 'Video' },
    { id: 'aging', label: 'Time to deliver' },
    { id: 'credits', label: 'Outstanding swing credits', align: 'right' },
    { id: 'actions', label: '', align: 'right' },
  ],
  [REQUEST_TABS.COMPLETE]: [
    { id: 'status', label: 'Status' },
    { id: 'golfer', label: 'Golfer' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'completed', label: 'Completed' },
    { id: 'turnaround', label: 'Turnaround' },
    { id: 'credits', label: 'Outstanding swing credits', align: 'right' },
  ],
}

const EMPTY_CAPTIONS = {
  [REQUEST_TABS.PURCHASED]: 'No purchases waiting on a video from the golfer.',
  [REQUEST_TABS.READY]: 'No videos are waiting for your analysis.',
  [REQUEST_TABS.COMPLETE]: 'You have not completed any virtual coaching yet.',
}

const RequestsTable = ({
  requests,
  tab,
  emptyTitle,
  emptyCaption,
  emptyAction,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const columns = COLUMNS[tab]

  if (requests.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <SectionEmptyState
          Icon={VideocamOutlinedIcon}
          title={emptyTitle || 'Virtual Coaching'}
          caption={emptyCaption || EMPTY_CAPTIONS[tab]}
          action={emptyAction}
          bordered={false}
        />
      </Box>
    )
  }

  const openRequest = request => history.push(getRequestPath(request.id))

  const renderCell = (column, request) => {
    const golfer = getStudentName(request.student)
    const overdue = isOverdue(request)

    switch (column.id) {
      case 'status':
        return <RequestStatusChip status={request.status} overdue={overdue} />
      case 'golfer':
        return golfer
      case 'purchased':
        return formatDate(request.purchasedAt)
      case 'video':
        return (
          <VideoThumbnail
            src={request.video?.thumbnailUrl}
            alt={`Video from ${golfer}`}
          />
        )
      case 'aging':
        return (
          <Typography
            variant="body2"
            className={overdue ? classes.overdue : undefined}
          >
            {getAgingLabel(request)}
          </Typography>
        )
      case 'analysis':
        return (
          <VideoThumbnail
            src={request.response?.thumbnailUrl}
            alt={`Analysis for ${golfer}`}
          />
        )
      case 'completed':
        return formatDate(request.completedAt)
      case 'turnaround':
        return formatTurnaroundDuration(getTurnaroundHours(request))
      case 'credits':
        return request.outstandingCredits ?? 0
      case 'actions':
        return (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={event => {
              event.stopPropagation()
              openRequest(request)
            }}
          >
            Review
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <Table aria-label="Virtual coaching requests">
      <TableHead>
        <TableRow>
          {columns.map(column => (
            <TableCell
              key={column.id}
              align={column.align}
              className={classes.headCell}
            >
              {column.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {requests.map(request => (
          <TableRow
            key={request.id}
            hover
            className={classes.row}
            onClick={() => openRequest(request)}
          >
            {columns.map(column => (
              <TableCell key={column.id} align={column.align}>
                {renderCell(column, request)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

RequestsTable.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
  tab: PropTypes.oneOf(Object.values(REQUEST_TABS)).isRequired,
  emptyTitle: PropTypes.string,
  emptyCaption: PropTypes.string,
  emptyAction: PropTypes.node,
}

export default RequestsTable
